import { motion, AnimatePresence } from "framer-motion";
import { Layers, Map, GraduationCap, Settings, ChevronRight, Plane, Zap } from "lucide-react";
import { useState } from "react";

const products = [
  {
    id: "g1000",
    icon: Layers,
    color: "oklch(0.28 0.09 240)",
    bg: "oklch(0.28 0.09 240 / 0.07)",
    badge: "Core Product",
    badgeBg: "oklch(0.28 0.09 240 / 0.1)",
    title: "G1000NXi Add-On",
    sub: "for Lockheed Martin Prepar3D",
    desc: "Full G1000NXi simulation with synthetic vision, LNAV/VNAV, AFCS, and complete failure injection. Built for professional ATO training.",
    items: ["PFD + MFD Simulation", "Synthetic Vision (SVT)", "Full AFCS & Autopilot", "Failure Injection"],
  },
  {
    id: "airports",
    icon: Map,
    color: "oklch(0.45 0.15 160)",
    bg: "oklch(0.45 0.15 160 / 0.07)",
    badge: "Airports",
    badgeBg: "oklch(0.45 0.15 160 / 0.1)",
    title: "Prepar3D Airport Development",
    sub: "Development and modification/update",
    desc: "Professional airport development and modification for Prepar3D. Custom scenery, navaids, ILS approaches and training layouts.",
    items: ["Custom Scenery", "Accurate Navaids", "ILS / Approach Data", "Training Layouts"],
  },
  {
    id: "cbt",
    icon: GraduationCap,
    color: "oklch(0.42 0.15 160)",
    bg: "oklch(0.42 0.15 160 / 0.07)",
    badge: "Training",
    badgeBg: "oklch(0.42 0.15 160 / 0.1)",
    title: "Computer Based Training",
    sub: "CBT for individual training and classes",
    desc: "Interactive CBT programs for individual and classroom aviation instruction. Aligned with FAA/EASA syllabus requirements.",
    items: ["Interactive Modules", "Individual & Class", "FAA/EASA Aligned", "Progress Tracking"],
  },
  {
    id: "custom",
    icon: Settings,
    color: "oklch(0.5 0.18 280)",
    bg: "oklch(0.5 0.18 280 / 0.07)",
    badge: "Custom",
    badgeBg: "oklch(0.5 0.18 280 / 0.1)",
    title: "Custom Avionics & Cockpit",
    sub: "Customer-tailored Aviation instruments",
    desc: "Customer-tailored aviation instruments and full cockpit simulation software upon request. Type-specific to any aircraft.",
    items: ["Custom Instruments", "Type-Specific Cockpit", "Any Authority Level", "Brand Customization"],
  },
];

const B737_FEATURES = [
  "Advanced FMC / MCDU Simulation",
  "EICAS Primary & Secondary Display",
  "Boeing-specific AFCS & Autopilot",
  "Full failure injection — all B737 systems",
  "FAA / EASA certification compatible",
  "Lockheed Martin Prepar3D integration",
  "Custom ATO branding & labeling",
  "Available for ATO partners",
];

export function ProductSection() {
  const [b737Open, setB737Open] = useState(false);
  const go = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="products" className="py-24">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 280, damping: 32 }}
          className="mb-12"
        >
          <p className="text-xs font-mono tracking-[0.25em] uppercase font-bold mb-2" style={{ color: "oklch(0.55 0.22 25)" }}>
            — Software Products —
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Professional <span style={{ color: "oklch(0.28 0.09 240)" }}>Suite</span>
          </h2>
        </motion.div>

        {/* ═══ B737 MAX HERO CARD ═══ */}
        <motion.div
          id="b737"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="mb-8 rounded-2xl overflow-hidden border-2 shadow-[0_12px_48px_oklch(0.28_0.09_240/0.18)]"
          style={{ borderColor: "oklch(0.55 0.22 25)" }}
        >
          {/* Top accent bar */}
          <div
            className="h-1.5 w-full"
            style={{ background: "linear-gradient(90deg, oklch(0.55 0.22 25) 0%, oklch(0.68 0.22 45) 50%, oklch(0.55 0.22 25) 100%)" }}
          />

          <div
            className="p-8 md:p-10"
            style={{ background: "linear-gradient(135deg, oklch(0.14 0.04 240) 0%, oklch(0.18 0.06 240) 100%)" }}
          >
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              {/* Left */}
              <div className="flex-1">
                {/* Badges row */}
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <motion.span
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="flex items-center gap-1.5 text-[11px] font-bold font-mono tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
                    style={{ background: "oklch(0.55 0.22 25 / 0.2)", color: "oklch(0.85 0.12 45)", border: "1px solid oklch(0.55 0.22 25 / 0.4)" }}
                  >
                    <Zap className="w-3 h-3" /> Now in Production
                  </motion.span>
                  <span
                    className="text-[11px] font-bold font-mono tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
                    style={{ background: "oklch(0.45 0.15 160 / 0.2)", color: "oklch(0.65 0.18 160)", border: "1px solid oklch(0.45 0.15 160 / 0.3)" }}
                  >
                    ✓ Available for Presentation
                  </span>
                  <span
                    className="text-[11px] font-bold font-mono tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
                    style={{ background: "oklch(0.65 0.2 240 / 0.15)", color: "oklch(0.7 0.2 200)", border: "1px solid oklch(0.65 0.2 240 / 0.3)" }}
                  >
                    Available for Presentation
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "oklch(0.55 0.22 25 / 0.15)", border: "2px solid oklch(0.55 0.22 25 / 0.4)" }}
                  >
                    <Plane className="w-7 h-7 -rotate-45" style={{ color: "oklch(0.8 0.15 45)" }} />
                  </div>
                  <div>
                    <h3
                      className="text-3xl md:text-4xl font-black leading-tight"
                      style={{ fontFamily: "'Space Grotesk', sans-serif", color: "oklch(0.95 0.02 240)" }}
                    >
                      B737 MAX{" "}
                      <span style={{ color: "oklch(0.8 0.15 45)" }}>Avionics</span>
                    </h3>
                    <p className="text-sm font-mono mt-1" style={{ color: "oklch(0.6 0.04 240)" }}>
                      Advanced Boeing 737 MAX Avionics Simulation — for Lockheed Martin Prepar3D
                    </p>
                  </div>
                </div>

                <p className="text-base leading-relaxed mb-6" style={{ color: "oklch(0.75 0.03 240)" }}>
                  Our latest product — a full-featured Boeing 737 MAX avionics simulation suite for Prepar3D.
                  Developed and ready for ATO demonstration. Includes EICAS, advanced FMC/MCDU, Boeing-specific
                  AFCS and complete failure injection across all systems. Compatible with FAA and EASA certification requirements.
                </p>

                {/* Features grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {B737_FEATURES.map(f => (
                    <div key={f} className="flex items-center gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "oklch(0.8 0.15 45)" }} />
                      <span className="text-sm" style={{ color: "oklch(0.75 0.03 240)" }}>{f}</span>
                    </div>
                  ))}
                </div>

                {/* Expand toggle */}
                <button
                  onClick={() => setB737Open(!b737Open)}
                  className="flex items-center gap-2 text-sm font-semibold mb-6 transition-all"
                  style={{ color: "oklch(0.7 0.2 200)" }}
                >
                  <motion.span animate={{ rotate: b737Open ? 90 : 0 }} transition={{ type: "spring", stiffness: 300, damping: 25 }}>
                    <ChevronRight className="w-4 h-4" />
                  </motion.span>
                  {b737Open ? "Hide technical details" : "Show technical details"}
                </button>

                <AnimatePresence>
                  {b737Open && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      className="overflow-hidden mb-6"
                    >
                      <div
                        className="rounded-xl p-5 border"
                        style={{ background: "oklch(0.1 0.03 240 / 0.8)", borderColor: "oklch(0.65 0.2 240 / 0.2)" }}
                      >
                        <p className="text-xs font-mono tracking-[0.2em] uppercase font-bold mb-4" style={{ color: "oklch(0.7 0.2 200)" }}>
                          Simulated Systems
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {[
                            { title: "Display Panels", items: ["PFD (Primary Flight Display)", "ND (Navigation Display)", "EICAS Upper & Lower", "CDU / MCDU"] },
                            { title: "Avionics & Navigation", items: ["IRS / ADC", "VHF-NAV / LOC / GS", "GPS (SBAS)", "XPDR / ATC", "DME / ADF"] },
                            { title: "Flight Control", items: ["Boeing AFCS Autopilot", "Autothrottle", "Flight Director", "Pitot-Static System", "All failure modes"] },
                          ].map(group => (
                            <div key={group.title}>
                              <p className="text-xs font-bold mb-2" style={{ color: "oklch(0.65 0.04 240)" }}>{group.title}</p>
                              <ul className="space-y-1">
                                {group.items.map(item => (
                                  <li key={item} className="flex items-center gap-2 text-xs" style={{ color: "oklch(0.6 0.03 240)" }}>
                                    <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: "oklch(0.7 0.2 200)" }} />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={go}
                    className="flex items-center justify-center gap-2 px-7 py-3 rounded-xl font-bold text-sm tracking-wide transition-all"
                    style={{
                      background: "linear-gradient(135deg, oklch(0.55 0.22 25) 0%, oklch(0.65 0.2 35) 100%)",
                      color: "#fff",
                      boxShadow: "0 6px 24px oklch(0.55 0.22 25 / 0.45)",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 8px 32px oklch(0.55 0.22 25 / 0.65)")}
                    onMouseLeave={e => (e.currentTarget.style.boxShadow = "0 6px 24px oklch(0.55 0.22 25 / 0.45)")}
                  >
                    Request Information <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={go}
                    className="flex items-center justify-center gap-2 px-7 py-3 rounded-xl font-bold text-sm tracking-wide border transition-all"
                    style={{
                      borderColor: "oklch(0.65 0.2 240 / 0.35)",
                      color: "oklch(0.7 0.2 200)",
                      background: "transparent",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = "oklch(0.65 0.2 240 / 0.08)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  >
                    Request Demo Presentation
                  </button>
                </div>
              </div>

              {/* Right — spec panel */}
              <div
                className="flex-shrink-0 w-full lg:w-72 rounded-2xl p-6 border"
                style={{ background: "oklch(0.1 0.03 240 / 0.7)", borderColor: "oklch(0.55 0.22 25 / 0.3)" }}
              >
                <p className="text-xs font-mono tracking-[0.2em] uppercase font-bold mb-4" style={{ color: "oklch(0.8 0.15 45)" }}>
                  Quick Specs
                </p>
                {[
                  { l: "Platform", v: "Prepar3D v4/v5/v6" },
                  { l: "OS", v: "Windows 10/11 x64" },
                  { l: "Display", v: "Multi-display" },
                  { l: "Certification", v: "FAA / EASA" },
                  { l: "Status", v: "Production" },
                  { l: "Demo", v: "Available" },
                ].map(row => (
                  <div key={row.l} className="flex justify-between items-center py-2.5 border-b last:border-none" style={{ borderColor: "oklch(0.65 0.2 240 / 0.15)" }}>
                    <span className="text-xs font-mono" style={{ color: "oklch(0.55 0.04 240)" }}>{row.l}</span>
                    <span
                      className="text-xs font-bold"
                      style={{ color: row.v === "Available" || row.v === "Production" ? "oklch(0.65 0.18 160)" : "oklch(0.78 0.03 240)" }}
                    >
                      {row.v}
                    </span>
                  </div>
                ))}

                <div
                  className="mt-5 rounded-xl p-4 text-center"
                  style={{ background: "oklch(0.55 0.22 25 / 0.1)", border: "1px solid oklch(0.55 0.22 25 / 0.3)" }}
                >
                  <p className="text-[11px] font-mono tracking-wider uppercase mb-1" style={{ color: "oklch(0.8 0.15 45)" }}>Contact Us</p>
                  <p className="text-xs" style={{ color: "oklch(0.6 0.04 240)" }}>Available for ATO partners.<br />Request information today.</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Standard products grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, type: "spring", stiffness: 280, damping: 32 }}
                className="bg-white rounded-xl border border-border p-7 hover:shadow-[0_8px_32px_oklch(0.12_0.03_240/0.1)] transition-all duration-300"
                style={{ borderLeft: `4px solid ${p.color}` }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl border border-border flex items-center justify-center" style={{ background: p.bg }}>
                    <Icon className="w-5 h-5" style={{ color: p.color }} />
                  </div>
                  <span
                    className="text-xs font-semibold font-mono tracking-wider px-3 py-1 rounded-full border"
                    style={{ color: p.color, background: p.badgeBg, borderColor: `color-mix(in oklch, ${p.color} 40%, transparent)` }}
                  >
                    {p.badge}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{p.title}</h3>
                <p className="text-xs text-muted-foreground font-mono mb-3">{p.sub}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{p.desc}</p>
                <div className="grid grid-cols-2 gap-1.5 mb-5">
                  {p.items.map(item => (
                    <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
                      {item}
                    </div>
                  ))}
                </div>
                <button onClick={go} className="flex items-center gap-1 text-sm font-semibold hover:gap-2 transition-all" style={{ color: p.color }}>
                  Request Info <ChevronRight className="w-4 h-4" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
